import type { RoofingServiceContent } from "@/components/RoofingServicePage";

export const roofingServicePages: Record<string, RoofingServiceContent> = {
  "roof-repair": {
    currentSlug: "roof-repair",
    heroTitle: "Roof Repair in Southern California",
    heroSubtitle: "Targeted fixes for leaks, cracked tile, lifted shingles and failing flashing – done by a local crew that's been on Southern California roofs since 1981.",
    intro: "Most Southern California roofs still have plenty of life left, they just need the right repair in the right spot. We track leaks to the actual source, fix what's failing, and leave the rest alone.",
    sections: [
      {
        title: "When roof repair is the right call",
        body: [
          "Roof repair makes sense when the structure is still solid and the damage is limited to a few spots. That covers a lot of Southern California homes – leaks around vents, skylights, chimneys and wall transitions are common, along with cracked tile and shingles that have lifted from years of heat.",
          "Homes in Pine Valley, Winter Gardens, Mission Valley and the older parts of the city see the same patterns year after year. If the framing and underlayment are still in decent shape, a focused repair almost always beats jumping to a full roof replacement.",
        ],
      },
      {
        title: "What our roof repair includes",
        body: [
          "We start by finding where water is actually getting in – not just where the ceiling stain shows up. From there, repairs may include replacing broken or slipped tile, resealing penetrations, redoing flashing, or fixing sections of underlayment that have started to fail.",
          "A lot of the leaks we see come from earlier work that wasn't done right the first time. We don't stack shortcuts on top of shortcuts. Repairs are built to hold up under Southern California sun, not just make it through the next rain.",
          "Materials matter here too. On tile roofs we match existing concrete or clay tile whenever we can, so a repair doesn't stand out on the roofline. On shingle roofs we use materials rated for high UV exposure, and on any flat sections tied into the main roof we use elastomeric coatings and cap sheet that hold up to standing heat instead of degrading in a couple of summers.",
        ],
      },
      {
        title: "How long a roof repair takes",
        body: [
          "Most roof repairs wrap up the same day. Some take a few hours. Tile roofs generally take a bit longer, flat roofs depend on access and how many layers are in play, and shingle repairs are usually the quickest.",
          "Once we're on the roof and can see what's really going on, we give you a straight answer on timing. If something turns into a bigger job than expected, you'll know before we keep going – not after.",
        ],
      },
      {
        title: "The Southern California problems we see over and over",
        body: [
          "Heat is rough on roofs here. Sealants dry out, underlayment gets brittle and shingles lose their grip. Santa Ana winds lift edges and ridge caps, coastal air corrodes metal, and inland heat cooks everything else.",
          "Older neighborhoods in Carlsbad, Encinitas, Oceanside and parts of Southern California usually have a mix of original materials and older repair work layered together. Local experience matters because these problems keep repeating themselves.",
        ],
      },
      {
        title: "Roof repair with solar in the picture",
        body: [
          "If your home already has solar, roof repair has to be handled carefully. In some cases panels need to be temporarily removed and reinstalled so the repair can be done right.",
          "If you're planning a solar installation, any roof issues should be handled first. You don't want to chase a leak later with panels in the way. We handle both roofing and solar, so repairs account for future attachment points and layout.",
        ],
      },
      {
        title: "What roof repair costs in Southern California",
        body: [
          "Pricing on roof repair comes down to a handful of things: how many separate problem areas there are, whether the roof is tile, shingle or flat, how steep and how tall the roof is, and how much of the underlayment underneath has already broken down. A single flashing leak on a single-story shingle roof is a very different job than three cracked tile sections and a valley on a two-story home.",
          "Access plays a bigger role than most homeowners expect. Steep pitches, tall second stories and roofs crowded with skylights or solar panels all add labor time. We inspect first and price the actual scope, not a guess from the driveway, so the number you get reflects the roof in front of us.",
        ],
        bullets: [
          "Number and location of leak points",
          "Roof type – tile, shingle or flat",
          "Pitch, height and access for the crew",
          "Condition of the underlayment underneath",
        ],
      },
      {
        title: "Warranty on our repair work",
        body: [
          "Every repair we complete comes with a written warranty covering our labor, and manufacturer warranties apply to any new materials we install. If a repair we made fails within the warranty period, we come back and fix it – that's built into the price from the start, not an upsell later.",
          "We keep records of what was repaired and where, so if you sell the home or file an insurance claim down the road, there's a clear history of the work instead of a vague memory of someone fixing it once.",
        ],
      },
    ],
  },

  "roof-replacement": {
    currentSlug: "roof-replacement",
    heroTitle: "Roof Replacement in Southern California",
    heroSubtitle: "Full tear-off and replacement with shingle, tile or flat roof systems built for Southern California heat, sun and coastal conditions.",
    intro: "Roof replacement usually shows up after years of small fixes start adding up. When repairs stop making sense, we tear the old system off, address the deck, and install a new roof that's actually built for how this county weathers roofs.",
    sections: [
      {
        title: "When roof replacement makes sense",
        body: [
          "Replacement usually comes up after a stretch of small fixes: leaks getting more frequent, tile cracking easier, shingles no longer holding, or underlayment that's worn out even when the surface still looks alright.",
          "We see this a lot on homes built in the 80s and 90s across Carlsbad, Pine Valley, Winter Gardens and Mission Valley. At that point, continuing to patch usually costs more over time than doing the replacement once and being done with it.",
        ],
      },
      {
        title: "What roof replacement includes",
        body: [
          "Roof replacement means removing the old roof – not laying new material over old problems. We tear off the existing system, inspect the deck, and repair soft or damaged areas. New underlayment goes down, flashing is replaced, and all penetrations are handled correctly.",
          "From there we install a new roof system chosen for the home and for Southern California conditions. Shingle, tile and flat systems all get handled differently, and ventilation and drainage matter more here than most people realize.",
          "We also walk you through material choices before tear-off starts: architectural shingle versus clay or concrete tile versus a TPO or modified bitumen system on a low-slope section. Color matters for heat too – lighter, cool-roof rated products reflect more sun and can make a real difference on an inland home that bakes all summer.",
        ],
      },
      {
        title: "How long a roof replacement takes",
        body: [
          "Most residential replacements take a few days. Some go faster, some take longer depending on the roof type, pitch and access. Tile roofs generally add time, and steep pitches do too.",
          "Weather and whatever we find after tear-off can shift the schedule. We explain that upfront and keep you posted as work moves along so nothing feels like a surprise.",
        ],
      },
      {
        title: "The Southern California problems we plan around",
        body: [
          "Sun is the biggest factor. UV breaks down roofing materials faster than most people expect, and underlayment failure is one of the most common things we run into – even when the surface still looks okay.",
          "Older framing, multiple past roof layers and homes built before modern ventilation standards all come into play. Inland heat hits harder than coastal air, and that changes what materials actually make sense.",
        ],
      },
      {
        title: "Replacing a roof with solar in mind",
        body: [
          "If you're planning solar, roof replacement should come first – always. Installing panels on a roof that's already near the end of its life just causes bigger problems later.",
          "We coordinate roof replacement and solar installation together so attachment points, layouts and penetrations are planned as one project, not two separate ones.",
        ],
      },
      {
        title: "Permits and inspections",
        body: [
          "A full roof replacement in Southern California requires a building permit, and most jurisdictions want a final inspection once the job wraps up. We pull the permit, schedule the inspection, and handle the paperwork with the city or county so it's not one more thing on your plate.",
          "HOA approval is its own step in a lot of the communities we work in, especially where roof color or tile profile is regulated. If your neighborhood has design guidelines, we can put together what's needed for HOA sign-off before material ever gets ordered, so there's no delay once the crew is scheduled.",
        ],
      },
      {
        title: "Warranty coverage on a new roof",
        body: [
          "New roof systems come with manufacturer warranties on materials, typically running anywhere from 25 years to lifetime depending on the product line you choose. On top of that we back our installation labor with our own workmanship warranty, since a good product installed wrong still fails early.",
          "We'll go over exactly what's covered, what voids coverage, and what maintenance – if any – is expected on your end to keep the manufacturer warranty valid. No fine print surprises after the crew leaves.",
        ],
      },
    ],
  },

  "roof-insurance-claims": {
    currentSlug: "roof-insurance-claims",
    heroTitle: "Roof Insurance Claim Help in Southern California",
    heroSubtitle: "Documentation, honest damage assessments and full repair or replacement work – with a licensed local crew that understands the claims process.",
    intro: "Filing a roof insurance claim goes smoother when the damage is documented correctly from day one. We inspect the roof, put together clear photo evidence, and coordinate directly with your adjuster so you're not fighting the process alone.",
    sections: [
      {
        title: "Professional inspection before you file",
        body: [
          "Every strong claim starts with clear documentation. We walk the full roofing system – shingles or tile, flashing, valleys, vents and underlayment – and check the attic when signs of water intrusion make it necessary.",
        ],
        bullets: [
          "Wind damage and lifted materials",
          "Cracked or displaced roof tiles",
          "Underlayment failure",
          "Flashing separation",
          "Active leaks and water staining",
        ],
      },
      {
        title: "Storm damage roof repair",
        body: [
          "Not every claim ends in a full replacement. Plenty of homes only need professional repair work.",
          "Across Fallbrook and Poway we often see wind lifting shingles. In Mission Valley heavy rain exposes drainage weak points. In older Encinitas and Oceanside neighborhoods, aging underlayment fails after big storms. When the damage is isolated, a proper repair extends the life of the roof – and we'll tell you honestly if it's more serious than that.",
        ],
      },
      {
        title: "Roof replacement through insurance",
        body: [
          "When storm damage hits a large section of the roofing system, insurance may approve a full replacement. That's common on older shingle systems and tile roofs where wind has shifted materials across the roof.",
          "If replacement is approved, we handle tear-off, new underlayment, flashing updates, ventilation improvements and full restoration to current California code. Clean work, clear communication, no surprises.",
        ],
      },
      {
        title: "Roofing and solar under one roof",
        body: [
          "Insurance claims get more complicated when solar panels are involved. As both a licensed roofer and a solar company, we handle everything in-house – solar removal and reinstallation, panel replacement if any are damaged, system inspection and proper roof integration.",
          "Keeping roofing and solar under one crew prevents delays and finger-pointing during the claims process.",
        ],
      },
      {
        title: "Our claim process",
        body: [
          "First, we perform a free roof inspection and document the damage. Second, we put together a detailed written scope of work. Third, we coordinate with your insurance adjuster when needed. Finally, once the claim is approved, we complete the repair or replacement.",
          "You'll always get a straight explanation of what qualifies, what doesn't, and what realistic next steps look like.",
        ],
      },
      {
        title: "What adjusters look for, and how we help",
        body: [
          "Adjusters are trained to separate storm damage from normal wear and tear, and that distinction is where a lot of claims get denied or underpaid. Cracked tile from a windstorm reads differently than cracked tile from twenty years of thermal cycling, and the difference usually comes down to pattern – scattered wind damage versus isolated wear in the same spots every time.",
          "We meet the adjuster on the roof whenever we can and point out exactly what we're seeing, tied to the storm date in question. Having a licensed roofer walk the roof alongside the adjuster, instead of the homeowner guessing at terminology, tends to produce a more accurate scope and fewer disputes over what's covered.",
        ],
      },
      {
        title: "If your claim gets denied or underpaid",
        body: [
          "A denial isn't always the final word. We've seen claims get reversed or supplemented once additional documentation – better photos, a moisture reading, a clearer explanation of the failure – gets in front of the insurance company. If we believe the damage is storm-related and the initial decision missed it, we'll put together a supplemental report you can submit.",
          "We're not a public adjuster and we won't promise an outcome, but we will give you an honest read on whether it's worth pushing back, and we'll document the roof accurately either way so you're arguing from facts, not frustration.",
        ],
      },
    ],
  },

  "emergency-roof-repair": {
    currentSlug: "emergency-roof-repair",
    heroTitle: "Emergency Roof Repair in Southern California",
    heroSubtitle: "Fast response for active leaks, storm damage and failed roofs – we stop the water first, then figure out what needs to happen next.",
    intro: "When water is coming in, the first priority is stopping it. We focus on securing loose materials, sealing exposed areas and putting temporary protection in place so more damage doesn't happen before permanent repairs are made.",
    sections: [
      {
        title: "When emergency repair is the right call",
        body: [
          "Emergency work is about stopping active water intrusion and preventing more damage. Sometimes we can make a permanent repair the same day. Other times it's a temporary fix until the roof dries out and we can properly evaluate it.",
          "Either way, you'll know what we did on the roof and what still needs attention.",
        ],
      },
      {
        title: "How fast we can be there",
        body: [
          "Safety comes first for our crew. We don't put people on unsafe roofs during high winds or active storms. Once conditions settle, emergency repairs are handled as quickly as we can get them scheduled.",
          "Most emergency work is completed the same day we get on-site. If follow-up work is needed after things dry out, we schedule it once we can see the whole picture and make smart decisions about repair vs. replacement.",
        ],
      },
      {
        title: "The emergencies we see most",
        body: [
          "Wind damage is number one. Ridge caps lift, tiles crack or slide, and shingles tear loose. Heavy rain finds dried-out flashing and failed sealant quickly.",
          "We also get called on emergencies caused by old repair work that finally gave out. Patches don't last forever, especially after years of Southern California heat and UV. When they fail, it usually happens all at once.",
        ],
      },
      {
        title: "What happens after the emergency",
        body: [
          "An emergency repair stops the immediate problem, but it doesn't always solve everything going on with the roof. Once things are stable we usually recommend a full inspection.",
          "That's when we decide whether a standard roof repair is enough or if the roof is heading toward replacement territory. The goal is to prevent repeat emergencies – not to keep patching the same spot every storm.",
        ],
      },
      {
        title: "What to expect when our crew shows up",
        body: [
          "We show up with tarps, felt, sealant and enough material to handle most on-the-spot fixes without a second trip. First we get eyes on the roof safely, then we walk the interior damage with you so we know what's actively getting wet right now, not just what looks bad.",
          "From there we stabilize the situation – securing loose tile or shingles, sealing open penetrations, tarping sections that can't be permanently fixed yet – and explain in plain terms what we did and what's left to address once the weather clears.",
        ],
        bullets: [
          "Temporary tarping over compromised sections",
          "Sealing active penetrations and flashing gaps",
          "Securing loose or lifted tile and shingles",
          "Clear notes on what still needs a follow-up visit",
        ],
      },
      {
        title: "Emergency repair costs and insurance timing",
        body: [
          "Emergency calls are priced based on scope, not a flat panic fee – a single tarp-and-seal job costs a lot less than stabilizing a roof after a tree limb comes through it. We'll give you a number before we start unless the situation is dangerous enough that stopping water has to happen immediately.",
          "If the damage came from a storm, take photos before we tarp anything and keep our invoice – both help if you end up filing a claim. We can also provide documentation after the fact that supports a claim tied to the same storm event.",
        ],
      },
    ],
  },

  "roof-inspection": {
    currentSlug: "roof-inspection",
    heroTitle: "Roof Inspection in Southern California",
    heroSubtitle: "Thorough, honest roof inspections with clear photo documentation and a straight answer on what actually needs to be done.",
    intro: "A lot of Southern California roofs look fine from the ground but have real issues starting underneath. A proper inspection catches those before water shows up inside – and before smaller problems turn into much bigger ones.",
    sections: [
      {
        title: "When a roof inspection makes sense",
        body: [
          "Inspections make sense in a lot of situations: before buying or selling a home, before installing solar, after a storm, or when the roof is getting older and you just want a straight answer on its condition.",
          "Sun and heat break materials down slowly, and problems often start out of sight. Getting eyes on it early is almost always cheaper than waiting.",
        ],
      },
      {
        title: "What our inspection covers",
        body: [
          "We look at the full system – not just what's visible from the ground. That includes roofing materials, flashing, penetrations, vents and overall stability. On tile roofs we pay close attention to what's happening underneath.",
          "We also check for past repairs, moisture intrusion and the areas that tend to fail first in Southern California heat. You'll get a clear explanation of what we see and what actually matters.",
          "Every inspection comes with photo documentation – close-ups of anything questionable, not just a verbal summary. That gives you something concrete to reference later, whether that's for your own records, a real estate transaction or a future contractor comparing notes on the roof's condition.",
        ],
      },
      {
        title: "How long an inspection takes",
        body: [
          "Most inspections take under an hour. Larger or more complex roofs – commercial buildings, tile systems, multi-level homes – take longer. We don't rush it.",
          "The point is to understand what's going on, not to check a box. If we find issues, we explain them on the spot whenever we can.",
        ],
      },
      {
        title: "Inspections before repairs or replacement",
        body: [
          "A roof inspection usually comes before any major decision. It's how we tell whether a targeted repair makes sense or if the roof is nearing replacement territory.",
          "Some roofs have years left in them. Others don't. The inspection is what makes that call something other than a guess.",
        ],
      },
      {
        title: "Inspections and solar planning",
        body: [
          "If solar is on your radar, inspect the roof first. Installing panels on a roof that's already near the end of its life just creates problems later.",
          "We inspect roofs with future solar installation in mind – looking at lifespan, attachment areas and likely problem spots before anything goes on top.",
        ],
      },
      {
        title: "Real estate and lender inspections",
        body: [
          "Buying or selling a home in Southern California almost always means a roof gets looked at somewhere in the process, and general home inspectors typically won't get on a tile roof or go into detail on roofing-specific issues. We provide a roofing-focused inspection report that buyers, sellers, agents and lenders can actually use to negotiate or clear a contingency.",
          "If the report turns up issues, we'll give you repair pricing on the spot so there's no back-and-forth waiting on a second visit. That's helped a lot of our clients close escrow without the roof becoming the thing that stalls the deal.",
        ],
      },
      {
        title: "How often you should get a roof inspected",
        body: [
          "As a rule of thumb, an annual inspection is reasonable for most roofs, and after any significant Santa Ana wind event or heavy rain is worth a look regardless of age. Tile roofs and flat roofs benefit from slightly more frequent checks since problems underneath the surface are harder to spot from the ground.",
          "Coastal homes near the marine layer deal with more corrosion on metal flashing and fasteners, while East County and inland homes see faster breakdown from direct heat. Either way, a quick inspection every year or two is cheap insurance against a much bigger repair bill later.",
        ],
      },
    ],
  },

  "roof-leak-repair": {
    currentSlug: "roof-leak-repair",
    heroTitle: "Roof Leak Repair in Southern California",
    heroSubtitle: "We track leaks to the real source – flashing, penetrations, underlayment – and fix what's actually failing instead of the ceiling stain.",
    intro: "Ceiling stains, damp drywall and water after a storm usually mean it's time for a leak repair. Sometimes leaks only show up during heavy weather. Sometimes they come and go for months. Either way, we find where the water is really getting in.",
    sections: [
      {
        title: "When leak repair is needed",
        body: [
          "If you're seeing stains on the ceiling, damp drywall or water showing up after rain, a leak repair is usually needed. Some leaks only appear during heavy storms or wind-driven rain.",
          "We also see leaks that come and go for months – usually tied to flashing, vents or dried-out sealants. Southern California sun speeds up that breakdown, especially on older roofs.",
        ],
      },
      {
        title: "Where roof leaks actually start",
        body: [
          "Most leaks don't come from the middle of the roof. They start around penetrations, edges and transitions – vents, skylights, chimneys and wall connections are common weak points.",
          "Past repair work is another big one. Old patches fail over time. Once heat and UV get involved, weak spots don't hold forever.",
        ],
      },
      {
        title: "What leak repair includes",
        body: [
          "It starts with tracking the source. We inspect the roof, not just the interior damage. That means checking flashing, underlayment, penetrations and the surrounding materials.",
          "Once we've found the source, the repair may involve replacing failed flashing, sealing penetrations, patching underlayment or correcting bad past work. The fix depends on what actually failed.",
        ],
      },
      {
        title: "How long a leak repair takes",
        body: [
          "Most leak repairs wrap up the same day. Some take a few hours. Others take longer if the leak is hidden or tied to multiple failure points. Tile roofs and older homes usually take more time to diagnose.",
          "We explain what we find and what the repair involves before we start moving forward.",
        ],
      },
      {
        title: "Southern California-specific leak issues",
        body: [
          "Heat is the biggest factor. Sealants dry out, underlayment gets brittle, and materials expand and contract every day. Over time that movement creates openings.",
          "We also see leaks after wind events, where rain gets pushed into places it normally wouldn't reach. That's why leaks sometimes appear suddenly after years of no problems.",
        ],
      },
      {
        title: "Leak repair vs. bigger roofing work",
        body: [
          "A single leak doesn't automatically mean the roof needs replacing. Plenty of them can be handled with targeted repair. But recurring leaks or multiple failure points can be a sign the roof is near the end of its life.",
          "A proper leak repair helps figure out which situation you're actually in.",
        ],
      },
      {
        title: "How we find a leak that's hard to trace",
        body: [
          "Some leaks show up feet away from where the water actually enters, especially on tile roofs where water can travel along the underlayment before dripping down. We trace stains, check the attic from the inside when we can access it, and sometimes run water on isolated sections of the roof to reproduce the leak and watch exactly where it shows up.",
          "It takes more time than a quick patch-and-hope approach, but it's the difference between fixing the actual problem and paying for the same repair again next winter.",
        ],
      },
      {
        title: "Interior damage and what to check after a leak",
        body: [
          "Once the roof side is handled, it's worth checking the interior for damage the water may have already caused – soft drywall, insulation that's been wet and lost its R-value, or wood framing that's started to discolor. We'll point out anything we notice while we're up there, though interior repair itself is usually handled by a separate contractor unless it's minor.",
          "Mold is the bigger concern with a leak that's been going on a while. If drywall or insulation has stayed damp for more than a day or two, it's worth having that checked rather than just painting over the stain once the roof is fixed.",
        ],
      },
    ],
  },

  "flat-roof-repair": {
    currentSlug: "flat-roof-repair",
    heroTitle: "Flat Roof Repair in Southern California",
    heroSubtitle: "TPO, torch-down, single-ply and low-slope systems – we handle seams, flashing and drainage problems on commercial and residential flat roofs.",
    intro: "Flat roofs take a beating in Southern California. Constant sun dries materials out, adhesives weaken and every day the roof expands and cools off. We fix the seams, edges and drainage that keep water from sitting where it shouldn't.",
    sections: [
      {
        title: "When flat roof repair makes sense",
        body: [
          "Flat roof repair usually comes up when you're seeing leaks, soft spots or areas where water isn't draining the way it should. Ponding water after rain is a big sign something is off.",
          "We see this on commercial buildings and on older homes with flat or low-slope sections. Southern California heat speeds up wear on flat systems, especially when water sits longer than it should.",
        ],
      },
      {
        title: "Common flat roof problems",
        body: [
          "Most flat roof issues start at seams, penetrations or edges. Membranes separate, flashing pulls loose, drains clog and water backs up.",
          "Past repairs are another common issue. Patch jobs that weren't done right don't hold up long-term under constant sun and temperature swings – and when they fail, leaks tend to come back in the same spots.",
        ],
      },
      {
        title: "What flat roof repair includes",
        body: [
          "It usually starts with walking the roof to see where water is actually collecting. We check seams, edges, drains and anywhere the surface feels soft underfoot. A lot of the real problems only show up once you're up there looking close.",
          "The repair itself depends on what's failing – resealing seams, fixing flashing, replacing a section of membrane or correcting drainage. The goal is to stop water from sitting where it shouldn't so the same leak doesn't keep coming back.",
        ],
      },
      {
        title: "How long the repair takes",
        body: [
          "Some flat roof repairs are quick. Others aren't. A lot depends on how much water got in and how long it's been sitting. Small seam or flashing repairs are often same-day.",
          "When we find trapped moisture under the membrane, things take longer. That's not something worth rushing. We explain what we find and how long the fix should realistically take before starting.",
        ],
      },
      {
        title: "Southern California-specific flat roof issues",
        body: [
          "Flat roofs take a beating here. Constant sun dries materials out, adhesives weaken and everything expands during the day and cools off at night. That movement adds up over time.",
          "Heavy rain also exposes drainage problems fast. Even a small slope issue can turn into standing water after a storm.",
        ],
      },
      {
        title: "Repair vs. replacement",
        body: [
          "Not every flat roof needs to be replaced right away. If the problem is isolated and the system still has life left, a targeted repair can buy real time.",
          "When leaks keep popping up in different areas or moisture is widespread, replacement usually makes more sense long-term. A proper repair helps figure out which side of that line you're on.",
        ],
      },
      {
        title: "Materials we work with on flat roofs",
        body: [
          "TPO, modified bitumen (torch-down) and single-ply EPDM each fail differently, so the repair approach changes with the material. TPO seams that have started to separate usually get heat-welded back together rather than caulked, since caulk alone doesn't hold up under a Southern California summer. Torch-down repairs typically involve cutting out the damaged section and properly lapping in new cap sheet, not just layering patch material on top.",
          "We carry material for all three systems on the truck for common repairs, and if your flat roof uses something less common, we'll identify the system correctly before quoting so the repair actually bonds to what's already up there instead of failing in a season.",
        ],
      },
      {
        title: "Preventing repeat problems on a flat roof",
        body: [
          "A lot of flat roof calls we get are the third or fourth repair on the same section, usually because drainage was never addressed the first time. Fixing a membrane tear without correcting the low spot or clogged drain that caused water to pool there just guarantees the same call next year.",
          "Once a repair is done, keeping drains and scuppers clear of debris – especially after Santa Ana wind events that blow leaves and dust across a roof – goes a long way toward making that repair actually last.",
        ],
      },
    ],
  },

  "roof-maintenance": {
    currentSlug: "roof-maintenance",
    heroTitle: "Roof Maintenance in Southern California",
    heroSubtitle: "Regular tune-ups that extend the life of your roof – resealing, flashing checks and small repairs handled before they turn into big ones.",
    intro: "Most roofs benefit from a periodic check-up, especially in Southern California's sun. Maintenance is about catching small issues early and keeping the roof working the way it should, not making it look brand new.",
    sections: [
      {
        title: "When maintenance makes sense",
        body: [
          "Maintenance is a smart move when the roof is still in decent shape but starting to show its age. Maybe it's been years since anyone looked at it. Maybe you've had small repairs before and want to avoid bigger ones.",
          "We see this a lot on homes with 10–20 year old roofs. Everything might still be holding, but heat, sun and time keep doing their work. Maintenance catches problems before they turn into calls you didn't plan on making.",
        ],
      },
      {
        title: "What maintenance includes",
        body: [
          "It usually starts with a walk-through of the roof to see what's actually going on. We check flashing, vents, sealants, edges and any areas that tend to fail first.",
          "That might mean resealing exposed spots, tightening or replacing flashing, clearing debris or handling small repairs before they spread. The goal is to keep the roof performing – not to make it perfect.",
        ],
      },
      {
        title: "How often maintenance is needed",
        body: [
          "There's no single schedule that fits every roof. Most benefit from maintenance every couple of years – sometimes more often if they're older or heavily sun-exposed. Tile roofs and flat sections usually need more attention.",
          "We'll tell you what makes sense based on the roof itself, not a calendar.",
        ],
      },
      {
        title: "Southern California-specific maintenance issues",
        body: [
          "Sun is the main driver. Sealants dry out faster, underlayment ages quicker and daily temperature swings cause materials to expand and contract. Over time those small movements create openings.",
          "We also see debris buildup after winds and storms. Even small blockages can push water somewhere it doesn't belong – maintenance catches those things early.",
        ],
      },
      {
        title: "Maintenance vs. repair or replacement",
        body: [
          "Maintenance is meant to delay bigger work, not replace it forever. If the roof is still solid, it can extend life and cut down how often you need actual repairs.",
          "If problems keep coming back despite maintenance, that's a sign the roof may be getting close to replacement territory. Maintenance helps make that call based on real condition instead of guesswork.",
        ],
      },
      {
        title: "What a maintenance visit costs",
        body: [
          "Maintenance visits are priced far below repair or replacement work since the scope is smaller and preventive – think resealing, minor flashing work and debris clearing rather than tear-off and new material. Roof size, pitch and whether it's tile, shingle or flat all factor into the price, the same as any other job.",
          "Homeowners who stay on a regular maintenance schedule generally spend less over the life of the roof than those who wait for something to fail, since small fixes cost less than emergency repairs and extend the timeline before a full replacement is needed.",
        ],
      },
      {
        title: "What we check during a maintenance visit",
        body: [
          "A thorough maintenance visit covers more than a quick glance. We check flashing around every penetration, look at sealant condition on vents and pipe boots, inspect ridge caps and hips for movement, and clear gutters and valleys of leaves and debris that build up after wind events.",
          "On tile roofs we check for slipped or cracked tile that hasn't caused a leak yet but will eventually. On shingle roofs we look for granule loss and lifted edges. It's the kind of routine attention that catches a five-dollar problem before it becomes a five-thousand-dollar one.",
        ],
        bullets: [
          "Flashing and sealant condition at every penetration",
          "Ridge cap and hip tile inspection",
          "Gutter, valley and drain clearing",
          "Early signs of granule loss or slipped tile",
        ],
      },
    ],
  },

  "residential-roofing": {
    currentSlug: "residential-roofing",
    heroTitle: "Residential Roofing in Southern California",
    heroSubtitle: "Repairs, replacements and full roofing service for single-family homes, townhouses and smaller multi-unit properties across Southern California.",
    intro: "Every home is a little different – different roof type, pitch and history. Residential roofing for us means figuring out what your specific roof actually needs and doing that work well, not selling you a bigger project than the situation calls for.",
    sections: [
      {
        title: "What residential roofing covers",
        body: [
          "Residential roofing includes everything from small repairs to full roof replacements – leaks, broken tile, worn shingles, underlayment that's finally had enough.",
          "We work on single-family homes, townhouses and smaller multi-unit properties. Each one's a little different depending on roof type, pitch and past work. The goal is always the same: fix what matters, don't overdo it.",
        ],
      },
      {
        title: "Common problems on Southern California homes",
        body: [
          "Heat does most of the damage here. Sealants dry out, flashing cracks and underlayment gets brittle long before people expect it to.",
          "A lot of older homes also have layers of past repairs. Some were done right. Some weren't. Those older fixes usually show up again once weather hits or the roof starts moving more with temperature changes.",
        ],
      },
      {
        title: "Repair vs. replacement",
        body: [
          "Not every home needs a new roof. Many issues can be handled with targeted roof repair if the structure and materials still have life left.",
          "When repairs keep stacking up or leaks start appearing in different areas, replacement usually makes more sense long-term. Residential roofing starts with figuring out which side of that line you're on.",
        ],
      },
      {
        title: "How residential projects usually go",
        body: [
          "Most jobs start with an inspection. That tells us what's actually going on and what options make sense. From there, we walk through scope, timing and what to expect.",
          "Some jobs are quick. Some take a few days. Tile roofs usually take longer than shingle. We explain that upfront so nothing about the schedule feels like a surprise.",
        ],
      },
      {
        title: "Solar and residential roofing",
        body: [
          "If solar is on your radar, roof condition matters first. Installing panels on a roof that's near the end just causes problems later.",
          "We handle residential roofing and solar installation together, so roof condition, attachment points and future access are part of the plan from day one. One crew, one plan, no finger-pointing later.",
        ],
      },
      {
        title: "HOA and neighborhood requirements",
        body: [
          "A good chunk of the neighborhoods we work in – parts of Rancho Bernardo, Rancho Bernardo, Ladera Ranch and plenty of planned communities – have HOA guidelines covering roof color, tile profile or material type. Skip that step and you can end up redoing work you already paid for.",
          "We're familiar with the common HOA requirements across Southern California and can put together the documentation – material samples, color specs, elevation photos – needed for approval before ordering material, so the timeline doesn't get held up mid-project.",
        ],
      },
      {
        title: "What to expect on install day",
        body: [
          "On repair days, expect a crew on the roof for a few hours to most of a day, some noise, and material staged in the driveway or side yard. On replacement days, expect dumpster or tear-off debris trailer placement, tarps over landscaping near the drop zone, and a full crew on-site from morning through evening.",
          "We walk you through what to expect before the crew shows up – where they'll park, what areas to keep clear, and roughly what the noise level will be – so the day doesn't catch anyone off guard.",
        ],
      },
    ],
  },

  "commercial-roofing": {
    currentSlug: "commercial-roofing",
    heroTitle: "Commercial Roofing in Southern California",
    heroSubtitle: "Repairs, maintenance and replacements for offices, warehouses, retail buildings and other commercial properties – flat, low-slope and everything in between.",
    intro: "Commercial roofs behave differently than residential ones, and they usually can't afford to shut a business down for a project. We plan around your operations, focus on the problem areas and keep communication clear the whole way through.",
    showMaterials: true,
    sections: [
      {
        title: "What commercial roofing covers",
        body: [
          "Commercial roofing covers repairs, maintenance and full replacements on offices, warehouses, retail buildings and other commercial properties. Flat and low-slope systems are common, and they behave differently than residential roofs.",
          "Leaks, drainage issues, worn membranes and failing flashing are the problems we see most. Work has to be planned so it doesn't disrupt daily operations more than necessary.",
        ],
      },
      {
        title: "Common commercial roof problems",
        body: [
          "Most issues start with water sitting somewhere it shouldn't. Drains clog, seams separate, and flashing pulls loose around units and walls. Once water starts pooling, materials break down fast.",
          "We also see a lot of older patch work on commercial roofs – temporary fixes that were never meant to last forever. Southern California sun and heat wear those down, and leaks usually come back in the same areas.",
        ],
      },
      {
        title: "Repair vs. replacement",
        body: [
          "Not every commercial roof needs replacing. Many issues can be handled with targeted repair if the system still has life left.",
          "When leaks keep popping up across the roof or moisture is widespread, replacement often makes more sense long-term. Commercial roofing starts with figuring out which situation you're actually in.",
        ],
      },
      {
        title: "How commercial projects usually go",
        body: [
          "Most start with an inspection to identify problem areas and decide whether repair or replacement makes sense. From there we walk through scope, timing and access.",
          "Some repairs are quick. Larger projects take more coordination. We work around business hours where possible and keep communication clear throughout the project.",
        ],
      },
      {
        title: "Southern California-specific commercial issues",
        body: [
          "Heat is rough on commercial roofs here. Membranes dry out faster, adhesives weaken and daily temperature changes cause constant movement. Over time that stress adds up.",
          "Heavy rain also exposes drainage problems fast. Even a small slope issue can lead to standing water and quicker material breakdown on a large commercial roof.",
        ],
      },
      {
        title: "Permits, inspections and code compliance",
        body: [
          "Commercial roofing work typically requires a permit through the local jurisdiction, and larger replacements may involve additional review depending on the building's occupancy type and the scope of work. We handle permit submission and coordinate inspections so the paperwork doesn't hold up your project or create issues down the line for insurance or a future sale.",
          "For properties with rooftop HVAC units, satellite equipment or solar, we also make sure any new roofing work is properly flashed and integrated around existing equipment rather than treated as an afterthought – that's a common source of failed inspections and callback leaks on commercial buildings.",
        ],
      },
      {
        title: "Scheduling around a working business",
        body: [
          "We know a roof project can't just shut a warehouse or retail space down. Most commercial work gets scheduled around your hours – early mornings, weekends, or phased section by section so part of the roof is always weathertight while we work on the rest.",
          "For multi-tenant buildings we coordinate with property management on tenant notices, noise windows and access, so the people working underneath the roof aren't surprised by a crew showing up. Clear scheduling up front is usually what keeps a commercial roofing project from becoming a headache for everyone involved.",
        ],
      },
    ],
  },
};
